/**
 * Insights — how the platform is actually being used.
 *
 * Answers the questions an admin keeps asking: are sign-ups turning into
 * users, do people come back, how often do they log in, where is the work
 * happening, and can I take the audit trail away as a file.
 *
 * Everything is derived on the server from the user table and the audit log,
 * so nothing here is a stored figure that can drift.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import {
  Activity, AlertTriangle, BarChart2, Download, RefreshCw, TrendingUp, UserCheck, Users, X,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { downloadAuditReport, fetchPlatformAnalytics } from './hooks/hooks';

const NAV = '#001f3f';
const TEAL = '#0d9488';
const GOLD = '#C9982A';

const WINDOWS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
];

const num = (value) => Number(value || 0).toLocaleString();
const pct = (value) => `${Number(value || 0).toFixed(1)}%`;
const shortDate = (iso) => (iso || '').slice(5);   // MM-DD

const Toast = ({ toast, onClose }) => {
  if (!toast.open) return null;
  const cls = toast.severity === 'success'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className="fixed top-4 right-4 z-50">
      <div className={`${cls} border rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 max-w-sm`}>
        <span className="text-sm font-medium">{toast.message}</span>
        <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-600">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const Tile = ({ icon, label, value, note, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center gap-2 text-slate-400">
      {icon}
      <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
    </div>
    <p className="text-2xl font-bold text-slate-800 mt-1.5 leading-tight">{value}</p>
    {note ? (
      <p className={`text-[11px] mt-1 ${tone === 'warn' ? 'text-amber-700' : 'text-slate-400'}`}>
        {note}
      </p>
    ) : null}
  </div>
);

const Panel = ({ title, hint, children, action }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-5 py-3.5 border-b border-slate-100 flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-bold text-slate-800">{title}</p>
        {hint ? <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p> : null}
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

function Insights() {
  const [days, setDays] = useState(90);
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const analytics = useQuery({
    queryKey: ['platform-analytics', days],
    queryFn: () => fetchPlatformAnalytics({ days }),
    retry: false,
  });

  const report = useMutation({
    mutationFn: () => downloadAuditReport({}),
    onSuccess: (name) => setToast({ open: true, message: `Downloaded ${name}.`, severity: 'success' }),
    onError: () => setToast({
      open: true, message: 'Could not build the audit report.', severity: 'error' }),
  });

  const data = analytics.data;
  const head = data?.headline || {};
  const health = data?.sign_in_health || {};

  const chartAxis = { fontSize: 11, fill: '#94A3B8' };

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <BarChart2 size={18} style={{ color: NAV }} /> Insights
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Growth, retention and activity, worked out from the user records and the audit trail.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {WINDOWS.map((option) => {
            const active = days === option.days;
            return (
              <button
                key={option.days}
                onClick={() => setDays(option.days)}
                className={`px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${
                  active ? 'text-white border-transparent' : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50'}`}
                style={active ? { backgroundColor: NAV } : undefined}
              >
                {option.label}
              </button>
            );
          })}
          <button onClick={() => analytics.refetch()}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 bg-white">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {analytics.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          Could not load the figures. Try again shortly.
        </div>
      ) : null}

      {analytics.isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
          Working out the numbers…
        </div>
      ) : data ? (
        <>
          {/* ── The figures an admin reads first ───────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Tile icon={<Users size={14} />} label="Total users" value={num(head.total_users)}
                  note={`${num(head.with_phone)} have a phone number`} />
            <Tile icon={<UserCheck size={14} />} label="Activation" value={pct(head.activation_rate)}
                  note={`${num(head.ever_logged_in)} of ${num(head.total_users)} ever logged in`} />
            <Tile icon={<Activity size={14} />} label="Active in 30 days" value={num(head.monthly_active)}
                  note={`${num(head.weekly_active)} this week · ${num(head.daily_active)} today`} />
            <Tile icon={<TrendingUp size={14} />} label="New in 30 days" value={num(head.new_last_30_days)}
                  note={`${pct(head.new_activation_rate)} of them came back`}
                  tone={head.new_activation_rate < 50 ? 'warn' : undefined} />
          </div>

          {/* ── Growth ─────────────────────────────────────────────────────── */}
          <Panel title="Sign-ups and activity"
                 hint="New accounts against people actually logging in, day by day.">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={chartAxis} minTickGap={24} />
                <YAxis tick={chartAxis} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="signups" name="Sign-ups" stroke={GOLD} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="active_users" name="People logging in" stroke={TEAL} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* ── Retention ────────────────────────────────────────────────── */}
            <Panel title="Retention by sign-up month"
                   hint="Of the people who joined that month, how many came back within a day, a week, a month.">
              {(data.retention || []).length === 0 ? (
                <p className="text-sm text-slate-400">No sign-ups in this period yet.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.retention}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={chartAxis} />
                      <YAxis tick={chartAxis} unit="%" domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="day_1_pct" name="Within a day" fill={NAV} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="day_7_pct" name="Within a week" fill={TEAL} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="day_30_pct" name="Within a month" fill={GOLD} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-slate-100">
                          {['Cohort', 'Signed up', '1 day', '7 days', '30 days'].map((h) => (
                            <th key={h} className="text-[10px] font-bold text-slate-400 uppercase tracking-wider py-2 pr-3">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {data.retention.map((row) => (
                          <tr key={row.cohort}>
                            <td className="py-2 pr-3 text-sm text-slate-700">{row.label}</td>
                            <td className="py-2 pr-3 text-sm text-slate-500">{num(row.signups)}</td>
                            <td className="py-2 pr-3 text-sm text-slate-700">{row.day_1} <span className="text-slate-400">({pct(row.day_1_pct)})</span></td>
                            <td className="py-2 pr-3 text-sm text-slate-700">{row.day_7} <span className="text-slate-400">({pct(row.day_7_pct)})</span></td>
                            <td className="py-2 pr-3 text-sm text-slate-700">{row.day_30} <span className="text-slate-400">({pct(row.day_30_pct)})</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Panel>

            {/* ── Engagement and frequency ─────────────────────────────────── */}
            <div className="space-y-5">
              <Panel title="Engagement grades"
                     hint="The same grades as User Management — how recently people logged in.">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.engagement} layout="vertical" margin={{ left: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={chartAxis} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" tick={chartAxis} width={70} />
                    <Tooltip />
                    <Bar dataKey="count" name="Users" radius={[0, 3, 3, 0]}>
                      {(data.engagement || []).map((row) => (
                        <Cell key={row.key} fill={row.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              <Panel title="How often people log in"
                     hint={`Logins per person over the last ${data.login_frequency?.window_days || 30} days — frequency, not just recency.`}>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={data.login_frequency?.buckets || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={chartAxis} />
                    <YAxis tick={chartAxis} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Users" fill={TEAL} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-slate-400 mt-2">
                  {num(data.login_frequency?.logins_total)} logins from{' '}
                  {num(data.login_frequency?.users_logging_in)} people.
                </p>
              </Panel>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* ── Where the work happens ───────────────────────────────────── */}
            <Panel title="Where the activity is"
                   hint="Records created, changed or removed, by part of the system.">
              {(data.activity_by_module || []).length === 0 ? (
                <p className="text-sm text-slate-400">Nothing recorded in this period.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.activity_by_module}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="app_label" tick={chartAxis} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={chartAxis} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="created" name="Created" stackId="a" fill={TEAL} />
                    <Bar dataKey="updated" name="Changed" stackId="a" fill={NAV} />
                    <Bar dataKey="deleted" name="Removed" stackId="a" fill="#DC2626" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>

            {/* ── Sign-in health and the report ────────────────────────────── */}
            <div className="space-y-5">
              <Panel title="Sign-in health"
                     hint="Failed sign-ins against successful ones. A sudden jump is worth a look.">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Successful</p>
                    <p className="text-xl font-bold text-slate-800">{num(health.successful)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Failed</p>
                    <p className="text-xl font-bold text-slate-800">{num(health.failed)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Failure rate</p>
                    <p className={`text-xl font-bold ${health.failure_rate > 25 ? 'text-amber-700' : 'text-slate-800'}`}>
                      {pct(health.failure_rate)}
                    </p>
                  </div>
                </div>
                {health.failure_rate > 25 ? (
                  <p className="text-[11px] text-amber-700 mt-3 flex items-center gap-1.5">
                    <AlertTriangle size={12} /> More than a quarter of sign-in attempts are failing.
                  </p>
                ) : null}
              </Panel>

              <Panel title="Audit report"
                     hint="The full trail — who changed what, when, and from which address.">
                <p className="text-sm text-slate-600 mb-4">
                  Downloads as a CSV you can keep or hand to an auditor. For a narrower report,
                  set the filters on the Audit Logs screen and download from there.
                </p>
                <button
                  onClick={() => report.mutate()}
                  disabled={report.isLoading}
                  className="flex items-center gap-2 bg-teal-600 text-white py-2.5 px-5 rounded-lg font-semibold text-sm hover:bg-teal-700 disabled:opacity-50"
                >
                  <Download size={14} />
                  {report.isLoading ? 'Preparing…' : 'Download audit report'}
                </button>
              </Panel>
            </div>
          </div>

          <p className="text-[11px] text-slate-400">
            Worked out when this page loaded. "People logging in" counts each person once a day,
            however many times they signed in.
          </p>
        </>
      ) : null}

      <Toast toast={toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
    </div>
  );
}

export default Insights;
